import { Injectable, inject } from '@angular/core';
import { HxSnackbarService } from '../controls/snackbar/snackbar-service';

/**
 * Error Service
 *
 * Centralized error handling service that manages error display and logging. Provides methods for displaying user
 * notifications and logging errors to monitoring services. Keeps UI concerns (snackbar) separate from interceptor
 * logic.
 *
 * @example
 *   ```typescript
 *   import { inject } from '@angular/core';
 *
 *   private readonly errorService = inject(ErrorService);
 *
 *   this.errorService.showError('Failed to load data');
 *   ```;
 */
@Injectable({ providedIn: 'root' })
export class ErrorService {
  private readonly snackbarService = inject(HxSnackbarService);

  /**
   * Display an error message to the user via snackbar
   *
   * @param message - Error message to display
   * @param duration - How long to show the message (default: 5000ms)
   */
  showError(message: string, duration: number = 5000): void {
    this.snackbarService.error(message, { duration });
  }

  /**
   * Display a success message to the user via snackbar
   *
   * @param message - Success message to display
   * @param duration - How long to show the message (default: 3000ms)
   */
  showSuccess(message: string, duration: number = 3000): void {
    this.snackbarService.success(message, { duration });
  }

  /**
   * Log error to console and/or external logging service (Application Insights, Sentry, etc.)
   *
   * @param error - Error to log
   * @param context - Additional context about where the error occurred
   */
  logError(error: any, context?: { url?: string; method?: string; component?: string }): void {
    console.error('[Error]', {
      error,
      context,
      timestamp: new Date().toISOString(),
    });

    // TODO: Add integration with Application Insights, Sentry, or other monitoring service
    // Example:
    // this.applicationInsights.trackException({ exception: error, properties: context });
  }
}
